import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
const IMPERSONATOR_EMAIL_KEY = 'koggelman_impersonator_email';
const IMPERSONATOR_NAME_KEY = 'koggelman_impersonator_name';
// Sticky top banner shown whenever the currently-loaded user session was
// entered via impersonation. Reads the `impersonatedByEmail` / `ByName` we
// stashed at impersonation-start (also decoded from the JWT on page reload).
export default function ImpersonationBanner() {
    const { user, setAuth, logout } = useAuthStore();
    const navigate = useNavigate();
    const [reverting, setReverting] = useState(false);
    // Prefer values read from the JWT (stashed on load) — survives refresh.
    const impersonatorEmail = localStorage.getItem(IMPERSONATOR_EMAIL_KEY) || user?.impersonatedByEmail || '';
    const impersonatorName = localStorage.getItem(IMPERSONATOR_NAME_KEY) || user?.impersonatedByDisplayName || '';
    if (!impersonatorEmail)
        return null;
    const revert = async () => {
        setReverting(true);
        try {
            const res = await api.post('/auth/end-impersonation');
            const { user: revertedUser, tokens } = res.data;
            // Store reverted tokens.
            localStorage.setItem('koggelman_access_token', tokens.accessToken);
            localStorage.setItem('koggelman_refresh_token', tokens.refreshToken);
            localStorage.setItem('koggelman_user', JSON.stringify(revertedUser));
            localStorage.removeItem(IMPERSONATOR_EMAIL_KEY);
            localStorage.removeItem(IMPERSONATOR_NAME_KEY);
            setAuth(revertedUser, tokens.accessToken, tokens.refreshToken);
            toast.success('Back to superadmin');
            // Hard reload so every cached React Query etc. refetches fresh.
            window.location.href = '/admin';
        }
        catch (e) {
            toast.error(e?.message || 'Failed to revert');
            // Last-resort escape hatch: full logout.
            logout();
            navigate('/login');
        }
        finally {
            setReverting(false);
        }
    };
    return (_jsxs("div", { role: "alert", style: {
            position: 'sticky',
            top: 0,
            zIndex: 80,
            background: 'linear-gradient(90deg, #dc2626 0%, #ea580c 100%)',
            color: '#fff',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
        }, children: [_jsx(Eye, { size: 18 }), _jsxs("div", { style: { flex: 1, fontSize: 13, lineHeight: 1.3 }, children: [_jsxs("strong", { children: ["Viewing as ", user?.displayName || user?.email] }), user?.role && _jsxs("span", { style: { opacity: 0.85 }, children: [" (", user.role, ")"] }), _jsxs("span", { style: { opacity: 0.75, marginLeft: 8 }, children: ["\u2014 impersonated by ", impersonatorName || impersonatorEmail] })] }), _jsxs("button", { type: "button", onClick: revert, disabled: reverting, style: {
                    background: '#fff', color: '#dc2626', border: 'none',
                    padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                    cursor: reverting ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                    minHeight: 36,
                }, children: [_jsx(X, { size: 14 }), " ", reverting ? '…' : 'Back to superadmin'] })] }));
}
